import React, { forwardRef, MouseEventHandler, PropsWithChildren } from 'react'

import { ImageParams } from '../../Common/ResponsiveImage'
import ModalBase from './ModalBase'
import ModalBody from './ModalBody'
import ModalContent from './ModalContent'
import ModalFooter from './ModalFooter'
import ModalHeader from './ModalHeader'
import ModalImageThumbnail from './ModalImageThumbnail'

export interface ModalProps {
  className?: string
  contentClassName?: string
  image?: App.Image
  imageParams?: ImageParams
  isFullHeight?: boolean
  isMaxHeight?: boolean
  isOpen?: boolean
  isPrimaryActionDisabled?: boolean
  isPrimaryActionHidden?: boolean
  isSecondaryActionDisabled?: boolean
  isSecondaryActionHidden?: boolean
  primaryActionLabel?: string
  primaryActionText?: string
  primaryActionHref?: string
  primaryActionTo?: string
  secondaryActionLabel?: string
  secondaryActionText?: string
  size?: 'large' | 'small'
  subtitle?: string
  title: string
  onAfterClose?: () => void
  onAfterOpen?: () => void;
  onBack?: (event?: React.MouseEvent<HTMLButtonElement>) => void
  onClose?: (event?: React.MouseEvent<HTMLButtonElement>) => void
  onPrimaryActionClick?: MouseEventHandler<HTMLButtonElement>
  onSecondaryActionClick?: MouseEventHandler<HTMLButtonElement>
  'data-testid'?: string;
}

/**
 * @description The common modal`.
 * @see `ModalBase.tsx`
 * @see `ModalHeader.tsx`
 * @see `ModalImageThumbnail.tsx`
 * @see `ModalBody.tsx`
 * @see `ModalContent.tsx`
 * @see `ModalFooter.tsx`
 * */
const Modal = forwardRef<HTMLDivElement, PropsWithChildren<ModalProps>>((props, ref) => {
  const {
    children,
    className,
    contentClassName,
    image,
    imageParams,
    isFullHeight,
    isMaxHeight,
    isOpen,
    isPrimaryActionDisabled,
    isPrimaryActionHidden,
    isSecondaryActionDisabled,
    isSecondaryActionHidden,
    primaryActionLabel,
    primaryActionText,
    primaryActionHref,
    primaryActionTo,
    secondaryActionLabel,
    secondaryActionText,
    size,
    subtitle,
    title,
    onAfterClose,
    onAfterOpen,
    onBack,
    onClose,
    onPrimaryActionClick,
    onSecondaryActionClick,
    'data-testid': testId,
  } = props

  return <ModalBase
    className={className}
    isFullHeight={isFullHeight}
    isMaxHeight={isMaxHeight}
    isOpen={isOpen}
    size={size}
    onAfterClose={onAfterClose}
    onAfterOpen={onAfterOpen}
    onClose={onClose}
  >
    <ModalHeader
      title={title}
      subtitle={subtitle}
      onBackButtonClick={onBack}
      onCloseButtonClick={onClose}
      data-testid={testId}
    />
    <ModalBody ref={ref}>
      {image && <ModalImageThumbnail image={image} imageParams={imageParams} />}
      {!!children && <ModalContent className={contentClassName}>
        {children}
      </ModalContent>}
    </ModalBody>
    {(onPrimaryActionClick || primaryActionHref || onSecondaryActionClick || primaryActionTo) && <ModalFooter
      isPrimaryActionHidden={isPrimaryActionHidden}
      isSecondaryActionHidden={isSecondaryActionHidden}
      primaryActionProps={{
        children: primaryActionText,
        'aria-label': primaryActionLabel,
        disabled: isPrimaryActionDisabled,
        onClick: onPrimaryActionClick,
        href: primaryActionHref,
        to: primaryActionTo,
      }}
      secondaryActionProps={{
        children: secondaryActionText,
        'aria-label': secondaryActionLabel,
        disabled: isSecondaryActionDisabled,
        onClick: onSecondaryActionClick,
      }}
    />}
  </ModalBase>
})

export default Modal
